import React from 'react';
import { useEthPrice } from '../hooks/useEthPrice';
import './EthAmount.css';

interface EthAmountProps {
  amount: number;
  showUsd?: boolean;
  variant?: 'default' | 'compact' | 'card';
  className?: string;
  colorClass?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

const formatNumber = (
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: options?.minimumFractionDigits ?? 4,
    maximumFractionDigits: options?.maximumFractionDigits ?? 4,
  }).format(value);
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

export const EthAmount: React.FC<EthAmountProps> = ({
  amount,
  showUsd = true,
  variant,
  className = '',
  colorClass,
  minimumFractionDigits,
  maximumFractionDigits,
}) => {
  const { price, loading, error } = useEthPrice();

  // Detect variant from className if not explicitly provided
  const detectedVariant = variant || (className === 'compact' || className === 'card' ? className : 'default');
  
  // Filter out variant classes from className to avoid duplication
  const additionalClasses = className.split(' ').filter(
    cls => cls && cls !== 'compact' && cls !== 'card'
  ).join(' ');

  const containerClass = [
    'eth-amount',
    detectedVariant !== 'default' ? detectedVariant : '',
    additionalClasses,
    colorClass || ''
  ]
    .filter(Boolean)
    .join(' ');

  const usdValue = price * amount;

  return (
    <div className={containerClass}>
      <div className="eth-value">
        <img src="/icon/logo-eth.png" alt="Ethereum" className="eth-logo" />
        <span className="amount">
          {formatNumber(amount, { minimumFractionDigits, maximumFractionDigits })} ETH
        </span>
      </div>

      {showUsd && (
        loading ? (
          <div className="loading">Fetching price…</div>
        ) : error ? (
          <div className="error">Price unavailable</div>
        ) : (
          <div className="usd-value">{formatCurrency(usdValue)}</div>
        )
      )}
    </div>
  );
};

export default EthAmount;


